import { useMemo, useState } from "react";
import { Layers, ListTodo, CheckCircle2, XCircle, MessageSquare, Paperclip } from "lucide-react";

import { TarefaStatus } from "@workflow/types";
import { PeriodoResumoAdminTarefas } from "@/types/tarefa.type";
import { useTarefaAdminResumoPeriodo } from "@/hooks/use-tarefa-admin-resumo-periodo";

import { PeriodoTarefaSelect } from "../components/PeriodoTarefaSelect";
import { TarefaKpiStrip } from "../components/TarefaKpiStrip";
import { TarefaVisaoGeralCard } from "../components/TarefaVisaoGeralCard";
import { People } from "../components/people";
import { TarefaStatusDonutCard } from "../components/TarefaStatusDonutCard";
import { TarefaDesempenhoCard } from "../components/TarefaDesempenhoCard";

export function PainelTarefasTab() {
  const [dias, setDias] = useState<PeriodoResumoAdminTarefas>(30);
  const { resumo, loading } = useTarefaAdminResumoPeriodo(dias);

  const totais = useMemo(() => {
    const porStatus = new Map<TarefaStatus, number>();
    let criadas = 0;
    for (const row of resumo.criadasPorDia) {
      porStatus.set(row.status, (porStatus.get(row.status) ?? 0) + row.total);
      criadas += row.total;
    }
    const concluidas = resumo.concluidasPorDia.reduce((soma, row) => soma + row.total, 0);

    return {
      criadas,
      abertas:
        (porStatus.get(TarefaStatus.PENDENTE) ?? 0) +
        (porStatus.get(TarefaStatus.EM_EXECUCAO) ?? 0),
      concluidas,
      canceladas: porStatus.get(TarefaStatus.CANCELADO) ?? 0,
    };
  }, [resumo]);

  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <PeriodoTarefaSelect value={dias} onChange={setDias} />
      </div>

      <TarefaKpiStrip
        items={[
          {
            key: "criadas",
            title: "Criadas no período",
            value: totais.criadas,
            icon: Layers,
            variant: "blue",
            loading,
          },
          {
            key: "abertas",
            title: "Ainda abertas",
            value: totais.abertas,
            icon: ListTodo,
            variant: "amber",
            loading,
          },
          {
            key: "concluidas",
            title: "Concluídas no período",
            value: totais.concluidas,
            icon: CheckCircle2,
            variant: "emerald",
            loading,
          },
          {
            key: "canceladas",
            title: "Canceladas",
            value: totais.canceladas,
            icon: XCircle,
            variant: "destructive",
            loading,
          },
          {
            key: "comentarios",
            title: "Comentários no período",
            value: resumo.totalComentarios,
            icon: MessageSquare,
            variant: "purple",
            loading,
          },
          {
            key: "documentos",
            title: "Documentos anexados",
            value: resumo.totalDocumentos,
            icon: Paperclip,
            variant: "default",
            loading,
          },
        ]}
      />

      <div className="grid items-start gap-4 lg:grid-cols-2">
        <TarefaVisaoGeralCard dias={dias} />
        <People dias={dias} />
      </div>

      <div className="grid items-start gap-4 lg:grid-cols-2">
        <TarefaStatusDonutCard dias={dias} />
        <TarefaDesempenhoCard dias={dias} />
      </div>
    </div>
  );
}
